/*
 * clinikoEmbed — Level 2 (CSP-build) wrapper around the Cliniko bookings iframe.
 *
 * Owns the postMessage channel for BOTH instances (the /contact/ inline panel and the
 * persistent booking drawer), which is why the handler lives here rather than in
 * booking-drawer.js — see components-klc.md §4.1.
 *
 * WHAT THIS COMPONENT DOES NOT DO: load the iframe. The inline instance server-renders a
 * real `src` with loading="lazy" and is fully working with this file absent — that is
 * the no-JS contract every [data-book] anchor on the site depends on. The drawer
 * instance carries `data-src`; promoting it is booking-drawer.js's job, on open. This
 * component only listens.
 *
 * Cliniko sends plain-string messages:
 *
 *   cliniko-bookings-resize:<px>   → set the frame height
 *   cliniko-bookings-page          → a step change; stamped for CSS/analytics ONLY
 *
 *   cliniko-bookings-page:confirmed → confirmed booking (not an ordinary step).
 * See https://help.cliniko.com/en/articles/4726326
 *
 * TWO PIECES OF HARDENING, both cheap and both load-bearing:
 *
 * 1. ORIGIN CHECK. `message` fires for EVERY frame on the page, so without validating
 *    event.origin any third-party iframe (an analytics tag, an embedded map) could spoof
 *    a resize. The expected origin is parsed from the iframe's own URL, so it tracks the
 *    CMS field and there is no second place to update when the Cliniko subdomain moves.
 *
 * 2. HEIGHT CLAMP. The px value is attacker-influenced if the origin check ever
 *    regresses, and a malformed message from Cliniko itself is just as plausible.
 *    Clamped to 320–4000px: below 320 the calendar is unusable, above 4000 nothing real
 *    is being rendered and we would be building a multi-megapixel element.
 *
 * Registered in resources/js/app.js as: Alpine.data('clinikoEmbed', clinikoEmbed)
 */

import { pushEvent } from '../analytics/datalayer.js';

/** Smallest usable calendar height, px. */
const MIN_FRAME_HEIGHT = 320;

/** Above this, no real calendar is being described. */
const MAX_FRAME_HEIGHT = 4000;

export default function clinikoEmbed() {
    return {
        /** @type {HTMLIFrameElement|null} */
        frame: null,

        /** @type {string|null} Origin parsed from the frame URL; null disables the channel. */
        expectedOrigin: null,

        /** @type {((event: MessageEvent) => void)|null} */
        messageHandler: null,
        loadHandler: null,
        confirmationTracked: false,

        init() {
            this.frame = this.$root.querySelector('[data-cliniko-frame]');

            if (!this.frame) {
                // The placeholder state — no URL was configured. Nothing to listen to.
                return;
            }

            this.expectedOrigin = this.originOf(
                this.frame.getAttribute('src') || this.frame.getAttribute('data-src')
            );

            if (!this.expectedOrigin) {
                return;
            }

            // Bound once and stored, so destroy() can remove exactly this listener.
            // The inline instance is re-created by Alpine.initTree() on every Swup
            // navigation to /contact/; without the matching removal each visit would
            // leave another live handler attached to window.
            this.messageHandler = (event) => this.onMessage(event);
            window.addEventListener('message', this.messageHandler);
            this.loadHandler = () => { this.confirmationTracked = false; };
            this.frame.addEventListener('load', this.loadHandler);
        },

        /**
         * The origin of a URL, or null if it is absent or unparseable.
         *
         * @param {string|null} url
         * @return {string|null}
         */
        originOf(url) {
            if (!url) {
                return null;
            }

            try {
                return new URL(url, window.location.href).origin;
            } catch {
                return null;
            }
        },

        /**
         * @param {MessageEvent} event
         */
        onMessage(event) {
            if (!this.frame || !this.expectedOrigin
                || event.origin !== this.expectedOrigin
                || event.source !== this.frame.contentWindow) {
                return;
            }

            if (typeof event.data !== 'string') {
                return;
            }

            if (event.data === 'cliniko-bookings-page:confirmed') {
                if (!this.confirmationTracked) {
                    this.confirmationTracked = true;
                    pushEvent('booking_complete', {
                        booking_location: this.$root.dataset.clinikoInstance === 'drawer' ? 'drawer' : 'inline',
                    });
                }
                return;
            }

            // A named non-confirmation step starts another booking journey.
            // The generic page notification alone must not re-arm confirmation.
            if (event.data.startsWith('cliniko-bookings-page:')) {
                this.confirmationTracked = false;
                this.$root.setAttribute('data-cliniko-step', 'changed');
                return;
            }

            if (event.data.startsWith('cliniko-bookings-resize:')) {
                this.resize(event.data.slice('cliniko-bookings-resize:'.length));

                return;
            }

            if (event.data === 'cliniko-bookings-page') {
                // A step changed. Stamped for CSS hooks and analytics only — it must not
                // resize, and it is not a conversion.
                this.$root.setAttribute('data-cliniko-step', 'changed');
            }
        },

        /**
         * @param {string} raw The px value Cliniko sent, unvalidated.
         */
        resize(raw) {
            const height = Number.parseInt(raw, 10);

            if (!Number.isFinite(height)) {
                return;
            }

            const clamped = Math.min(Math.max(height, MIN_FRAME_HEIGHT), MAX_FRAME_HEIGHT);

            this.frame.style.height = `${clamped}px`;
        },

        destroy() {
            if (this.frame && this.loadHandler) {
                this.frame.removeEventListener('load', this.loadHandler);
                this.loadHandler = null;
            }
            if (this.messageHandler) {
                window.removeEventListener('message', this.messageHandler);
                this.messageHandler = null;
            }

            this.frame = null;
        },
    };
}
